import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { wholesaleProducts } from '../../services/api';
import { formatProductDisplay } from '../../utils/formatProduct';
import { useCart } from '../../contexts/CartContext';
import { Wine, ArrowLeft, FileText, ShoppingCart } from 'lucide-react';
import ProductDrawer from '../../components/customer/ProductDrawer';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { cart, addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [activeTab, setActiveTab] = useState('tasting');
  const [added, setAdded] = useState(false);

  // Order side panel (ProductDrawer)
  const [drawerProduct, setDrawerProduct] = useState(null);
  const drawerRequestId = useRef(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    wholesaleProducts.get(id)
      .then(res => {
        const data = res.data.data;
        if (!data) {
          setNotFound(true);
        } else {
          setProduct(data);
        }
      })
      .catch(err => {
        if (err.response && err.response.status === 404) {
          setNotFound(true);
        } else {
          setError('Failed to load product. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const minQty = product ? (product.wholesaleMinQty || 1) : 1;
  const [qty, setQtyLocal] = useState(1);

  // Re-init qty when product loads
  useEffect(() => {
    if (product) {
      setQtyLocal(Math.max(product.wholesaleMinQty || 1, 1));
    }
  }, [product]);

  const decrement = () => setQtyLocal(q => Math.max(minQty, q - 1));
  const increment = () => setQtyLocal(q => q + 1);

  const handleAddToCart = () => {
    addToCart(product.productID, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  // Determine applicable tier (highest qualifying tier for current qty)
  const getApplicableTier = (tiers, currentQty) => {
    if (!tiers || tiers.length === 0) return null;
    const sorted = [...tiers].sort((a, b) => b.minQty - a.minQty);
    return sorted.find(t => currentQty >= t.minQty) || null;
  };

  const formatPrice = (val) =>
    typeof val === 'number' ? val.toFixed(2) : parseFloat(val || 0).toFixed(2);

  // Loading
  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // 404
  if (notFound) {
    return (
      <div className="container py-5 text-center">
        <Wine size={56} className="text-muted mb-3" />
        <h5 className="mb-3">Product not found</h5>
        <Link to="/wholesale/catalog" className="btn btn-outline-secondary">
          <ArrowLeft size={16} className="me-1" />
          Back to Catalog
        </Link>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{error}</div>
        <Link to="/wholesale/catalog" className="btn btn-outline-secondary">
          <ArrowLeft size={16} className="me-1" />
          Back to Catalog
        </Link>
      </div>
    );
  }

  if (!product) return null;

  const {
    productID, proBrand, proFanciful, proVarietal, proVintage, proAppellation, proVineyard,
    proWineType, proPack, proAlc, proWinemaker, inventory, wholesaleMinQty,
    displayPrice, hasOverride, proTastingNotes, proDescription, proTechSheetUrl,
    proLabelImageUrl, tiers = []
  } = product;

  const { primary: pdPrimary, secondary: pdSecondary } = formatProductDisplay(product);

  const inCartQty = cart[productID] || 0;
  const applicableTier = getApplicableTier(tiers, qty);
  const effectivePrice = applicableTier ? applicableTier.pricePerCase : displayPrice;

  return (
    <div className="container py-4">
      {/* Back link */}
      <div className="mb-3">
        <Link to="/wholesale/catalog" className="btn btn-link btn-sm text-decoration-none ps-0">
          <ArrowLeft size={16} className="me-1" />
          Back to Catalog
        </Link>
      </div>

      {/* Main section */}
      <div className="row g-4 mb-4">
        {/* Left: image */}
        <div className="col-md-5">
          {proLabelImageUrl ? (
            <img
              src={proLabelImageUrl}
              alt={proFanciful}
              className="img-fluid rounded"
              style={{ maxHeight: 420, objectFit: 'contain', background: '#f8f8f8', width: '100%' }}
            />
          ) : (
            <div
              className="rounded d-flex align-items-center justify-content-center bg-light"
              style={{ height: 320 }}
            >
              <Wine size={72} className="text-muted" />
            </div>
          )}
        </div>

        {/* Right: details */}
        <div className="col-md-7">
          <h3 className="mb-1">{pdPrimary}</h3>
          {pdSecondary && <p className="text-muted mb-3">{pdSecondary}</p>}

          {/* Price */}
          <div className="mb-3">
            <span className="text-muted small me-2">Your Price:</span>
            <span className="fs-4 fw-bold">${formatPrice(effectivePrice)} / case</span>
            {hasOverride && (
              <span className="badge bg-primary ms-2">Custom Price</span>
            )}
          </div>

          {/* Volume pricing table */}
          {tiers.length > 0 && (
            <div className="mb-3">
              <p className="small fw-semibold mb-1">Volume Pricing</p>
              <table className="table table-sm table-bordered mb-1">
                <thead className="table-light">
                  <tr>
                    <th>Min Cases</th>
                    <th>Price / Case</th>
                  </tr>
                </thead>
                <tbody>
                  {[...tiers]
                    .sort((a, b) => a.minQty - b.minQty)
                    .map(tier => {
                      const isApplicable = applicableTier && tier.tierID === applicableTier.tierID;
                      return (
                        <tr
                          key={tier.tierID}
                          className={isApplicable ? 'table-success fw-semibold' : ''}
                        >
                          <td>{tier.minQty}+</td>
                          <td>${formatPrice(tier.pricePerCase)}</td>
                        </tr>
                      );
                    })}
                </tbody>
                <caption className="caption-bottom small text-muted">
                  Prices update automatically based on quantity
                </caption>
              </table>
            </div>
          )}

          {/* Inventory */}
          <p className="mb-1">
            <span className="badge bg-light text-dark border me-1">{inventory} cases in stock</span>
          </p>

          {/* Min order note */}
          {wholesaleMinQty > 1 && (
            <p className="small text-muted mb-3">Minimum order: {wholesaleMinQty} cases</p>
          )}

          {/* In cart badge */}
          {inCartQty > 0 && (
            <div className="mb-2">
              <span className="badge bg-success">
                <ShoppingCart size={12} className="me-1" />
                In cart: {inCartQty} case{inCartQty !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Qty stepper */}
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <div className="input-group" style={{ width: 'auto' }}>
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={decrement}
                aria-label="Decrease quantity"
              >
                &minus;
              </button>
              <input
                type="number"
                className="form-control text-center"
                style={{ width: 70 }}
                value={qty}
                min={minQty}
                onChange={e => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val) && val >= minQty) setQtyLocal(val);
                }}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={increment}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>

          {/* Add to cart button */}
          <button
            className={`btn btn-lg w-100 ${added ? 'btn-success' : 'btn-primary'}`}
            onClick={handleAddToCart}
          >
            {added ? 'Added to Cart!' : 'Add to Cart'}
          </button>

          {/* Order side panel trigger */}
          <button
            className="btn btn-outline-secondary btn-lg w-100 mt-2"
            onClick={() => {
              drawerRequestId.current++;
              setDrawerProduct(product);
            }}
          >
            Order This Wine
          </button>
        </div>
      </div>

      {/* Order side panel */}
      <ProductDrawer
        product={drawerProduct}
        loading={false}
        onClose={() => {
          drawerRequestId.current++;
          setDrawerProduct(null);
        }}
        onAddToCart={addToCart}
      />

      {/* Tabs */}
      <div className="card">
        <div className="card-header p-0">
          <ul className="nav nav-tabs card-header-tabs ms-0" role="tablist">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'tasting' ? 'active' : ''}`}
                onClick={() => setActiveTab('tasting')}
                role="tab"
              >
                Tasting Notes
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'techsheet' ? 'active' : ''}`}
                onClick={() => setActiveTab('techsheet')}
                role="tab"
              >
                <FileText size={14} className="me-1" />
                Tech Sheet
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
                role="tab"
              >
                Details
              </button>
            </li>
          </ul>
        </div>
        <div className="card-body">
          {/* Tasting Notes */}
          {activeTab === 'tasting' && (
            <div>
              {proTastingNotes
                ? <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>{proTastingNotes}</p>
                : <p className="text-muted mb-0">No tasting notes available.</p>
              }
            </div>
          )}

          {/* Tech Sheet */}
          {activeTab === 'techsheet' && (
            <div>
              {proTechSheetUrl ? (
                <a
                  href={proTechSheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary"
                >
                  <FileText size={16} className="me-2" />
                  Download Tech Sheet
                </a>
              ) : (
                <p className="text-muted mb-0">No tech sheet available.</p>
              )}
            </div>
          )}

          {/* Details */}
          {activeTab === 'details' && (
            <table className="table table-sm mb-0">
              <tbody>
                <tr>
                  <th style={{ width: '35%' }}>Varietal</th>
                  <td>{proVarietal || '—'}</td>
                </tr>
                <tr>
                  <th>Appellation</th>
                  <td>{proAppellation || '—'}</td>
                </tr>
                {proVineyard && (
                  <tr>
                    <th>Vineyard</th>
                    <td>{proVineyard}</td>
                  </tr>
                )}
                <tr>
                  <th>Alcohol %</th>
                  <td>{proAlc ? `${proAlc}%` : '—'}</td>
                </tr>
                <tr>
                  <th>Pack Size</th>
                  <td>{proPack || '—'}</td>
                </tr>
                {proWinemaker && (
                  <tr>
                    <th>Winemaker</th>
                    <td>{proWinemaker}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
